'use client';
// ^-- to make sure we can mount the Provider from a server component
import type {QueryClient} from '@tanstack/react-query';
import {QueryClientProvider} from '@tanstack/react-query';
import {createTRPCClient, httpBatchLink} from '@trpc/client';
import {createTRPCContext} from '@trpc/tanstack-react-query';
import {useState} from 'react';
import superjson from 'superjson';
import {makeQueryClient} from './query-client';
import type {AppRouter} from './routers/_app';
export const {TRPCProvider, useTRPC} = createTRPCContext<AppRouter>();
let browserQueryClient: QueryClient;
export function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return makeQueryClient();
  }
  // Browser: make a new query client if we don't already have one
  // This is very important, so we don't re-make a new client if React
  // suspends during the initial render. This may not be needed if we
  // have a suspense boundary BELOW the creation of the query client
  if (!browserQueryClient) browserQueryClient = makeQueryClient();
  return browserQueryClient;
}
function getUrl() {
  const base = (() => {
    if (typeof window !== 'undefined') return '';
    if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`;
    return `http://localhost:${process.env.PORT ?? 8080}`;
  })();
  return `${base}/api/internal/trpc`;
}

async function getHeaders() {
  // On the server, forward cookies from the incoming request
  if (typeof window === 'undefined') {
    const {cookies} = await import('next/headers');
    const cookieStore = await cookies();
    return {
      cookie: cookieStore.toString(),
    };
  }
  return {};
}

export function TRPCReactProvider(
  props: Readonly<{
    children: React.ReactNode;
  }>,
) {
  // NOTE: Avoid useState when initializing the query client if you don't
  //       have a suspense boundary between this and the code that may
  //       suspend because React will throw away the client on the initial
  //       render if it suspends and there is no boundary
  const queryClient = getQueryClient();
  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        httpBatchLink({
          transformer: superjson,
          url: getUrl(),
          headers: getHeaders,
        }),
      ],
    }),
  );
  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {props.children}
      </TRPCProvider>
    </QueryClientProvider>
  );
}
